import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import client from "@/api/client";
import { Newspaper, CalendarClock, ExternalLink, RefreshCw, Flame, Maximize2, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { TradingViewTape, TradingViewSymbolInfo, TradingViewGoldChart } from "@/components/TradingViewWidgets";

// Symbols available in the big-chart modal — quick-switch board
const CHART_SYMBOLS = [
  { symbol: "OANDA:XAUUSD",  label: "Gold" },
  { symbol: "OANDA:XAGUSD",  label: "Silver" },
  { symbol: "TVC:DXY",       label: "DXY" },
  { symbol: "TVC:US10Y",     label: "US 10Y" },
  { symbol: "CME_MINI:ES1!", label: "S&P" },
  { symbol: "CME_MINI:NQ1!", label: "Nasdaq" },
  { symbol: "NYMEX:CL1!",    label: "Oil" },
  { symbol: "BITSTAMP:BTCUSD", label: "BTC" },
  { symbol: "FX:EURUSD",     label: "EUR/USD" },
  { symbol: "FX:USDJPY",     label: "USD/JPY" },
];

const IMPACT_STYLES = {
  high: "border-[#D4AF37] text-[#D4AF37]",
  medium: "border-[#AA8A25]/70 text-[#AA8A25]",
  low: "border-[#333] text-neutral-500",
  holiday: "border-neutral-800 text-neutral-500",
};

const COUNTRY_FLAG = {
  USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", JPY: "🇯🇵", CNY: "🇨🇳",
  CHF: "🇨🇭", CAD: "🇨🇦", AUD: "🇦🇺", NZD: "🇳🇿", All: "🌐",
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function groupByDay(events) {
  const groups = {};
  events.forEach((e) => {
    const day = (e.time || "").slice(0, 10) || "TBD";
    (groups[day] = groups[day] || []).push(e);
  });
  return groups;
}

export default function IntelligencePage() {
  const [headlines, setHeadlines] = useState([]);
  const [calendar, setCalendar] = useState({ events: [], from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | gold | forex
  const [bigChart, setBigChart] = useState(null); // { symbol, label } | null

  const load = async () => {
    setLoading(true);
    try {
      const [h, c] = await Promise.all([
        client.get("/intel/headlines"),
        client.get("/intel/calendar"),
      ]);
      setHeadlines(h.data.items);
      setCalendar(c.data);
    } catch (e) {
      toast.error("Failed to load intel");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = headlines.filter((h) => {
    if (filter === "all") return true;
    if (filter === "gold") return ["gold", "commodities"].includes(h.category);
    if (filter === "forex") return ["forex", "calendar"].includes(h.category);
    return true;
  });

  const highImpact = calendar.events.filter((e) => e.impact === "high");
  const mediumImpact = calendar.events.filter((e) => e.impact === "medium");
  const grouped = groupByDay([...highImpact, ...mediumImpact]);

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />

      {/* LIVE TICKER TAPE — streams from TradingView, updates every tick 24/7 */}
      <div className="mb-8 -mx-4 md:-mx-6" data-testid="intel-ticker">
        <TradingViewTape />
      </div>

      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">◇ Live Intelligence</p>
          <h1 className="font-display text-5xl md:text-6xl mt-1">The Trading Floor</h1>
          <p className="text-neutral-500 mt-2 max-w-xl">
            Gold-forward streaming prices, live headlines, this week's high-impact calendar — all tick-by-tick from TradingView.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn-outline-gold text-xs" data-testid="intel-refresh">
          <RefreshCw size={12} className={`inline mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* LIVE GOLD CHART — full streaming TradingView chart */}
      <div className="mb-4 border border-[#1f1f1f]" data-testid="intel-gold-chart">
        <div className="px-4 py-2 border-b border-[#1f1f1f] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[#D4AF37]">◇</span>
            <p className="overline text-[#D4AF37]">Spot Gold · XAU/USD · Live</p>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-[9px] uppercase tracking-widest text-neutral-600 hidden sm:inline">
              Streaming · powered by TradingView
            </p>
            <button
              onClick={() => setBigChart({ symbol: "OANDA:XAUUSD", label: "Gold" })}
              data-testid="expand-gold-chart"
              className="text-xs uppercase tracking-widest text-neutral-400 hover:text-[#D4AF37] transition inline-flex items-center gap-1.5 border border-[#1f1f1f] hover:border-[#D4AF37] px-3 py-1.5">
              <Maximize2 size={12} /> Big Chart
            </button>
          </div>
        </div>
        <TradingViewGoldChart symbol="OANDA:XAUUSD" interval="60" height={480} />
      </div>


      {/* Quick-launch strip: click any symbol to open the big chart */}
      <div className="mb-10">
        <p className="overline mb-3">◇ Quick Chart</p>
        <div className="flex flex-wrap gap-2" data-testid="chart-quicklaunch">
          {CHART_SYMBOLS.map((s) => (
            <button key={s.symbol}
              onClick={() => setBigChart(s)}
              data-testid={`quicklaunch-${s.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              className="text-xs uppercase tracking-widest text-neutral-400 hover:text-[#D4AF37] border border-[#1f1f1f] hover:border-[#D4AF37] px-3 py-1.5 transition">
              <Maximize2 size={11} className="inline mr-1.5" />{s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-10">
        {/* HEADLINES */}
        <section data-testid="intel-headlines">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <Newspaper size={16} className="text-[#D4AF37]" />
              <h2 className="font-display text-3xl">Headlines</h2>
            </div>
            <div className="flex items-center gap-1 border border-[#1f1f1f] p-0.5 text-[10px] uppercase tracking-widest">
              {["all", "gold", "forex"].map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  data-testid={`intel-filter-${f}`}
                  className={`px-3 py-1.5 transition ${filter === f ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "text-neutral-500 hover:text-neutral-200"}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>

          {loading && <p className="text-neutral-500 text-sm">Loading headlines…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-neutral-500 text-sm">No headlines matched.</p>
          )}

          <ol className="space-y-4">
            {filtered.slice(0, 40).map((h, i) => (
              <li key={i} className="border-l-2 border-[#1f1f1f] hover:border-[#D4AF37] pl-5 py-1 transition group"
                  data-testid={`intel-headline-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] uppercase tracking-widest ${
                    h.category === "gold" ? "text-[#D4AF37]" :
                    h.category === "commodities" ? "text-[#AA8A25]" :
                    "text-neutral-500"
                  }`}>◇ {h.category}</span>
                  <span className="text-[10px] text-neutral-600">{h.source}</span>
                  {h.published_at && (
                    <span className="text-[10px] text-neutral-600 ml-auto">{timeAgo(h.published_at)}</span>
                  )}
                </div>
                <a href={h.link} target="_blank" rel="noopener noreferrer"
                   className="font-body text-neutral-200 group-hover:text-white text-[15px] leading-snug inline-flex items-start gap-1.5">
                  {h.title}
                  <ExternalLink size={11} className="text-neutral-600 group-hover:text-[#D4AF37] mt-1 shrink-0" />
                </a>
              </li>
            ))}
          </ol>
        </section>

        {/* CALENDAR */}
        <aside data-testid="intel-calendar" className="lg:sticky lg:top-24 self-start">
          <div className="flex items-center gap-3 mb-5">
            <CalendarClock size={16} className="text-[#D4AF37]" />
            <h2 className="font-display text-3xl">This Week</h2>
          </div>
          {calendar.error && (
            <p className="text-red-400 text-xs mb-3">{calendar.error}</p>
          )}
          <p className="overline mb-4">
            {calendar.from && calendar.to ? `${calendar.from} → ${calendar.to}` : ""}
          </p>

          {highImpact.length === 0 && mediumImpact.length === 0 && !loading && (
            <p className="text-neutral-500 text-sm">No high-impact events this week.</p>
          )}

          <div className="space-y-6">
            {Object.entries(grouped).map(([day, evs]) => {
              const d = new Date(day + "T00:00:00Z");
              const label = isNaN(d.getTime()) ? day :
                d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" });
              return (
                <div key={day} data-testid={`cal-day-${day}`}>
                  <p className="overline text-[#AA8A25] mb-2">◇ {label}</p>
                  <ul className="space-y-2">
                    {evs.map((e, i) => (
                      <li key={i} className={`border ${IMPACT_STYLES[e.impact] || IMPACT_STYLES.low} bg-[#0a0a0a] px-3 py-2`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg leading-none">{COUNTRY_FLAG[e.country] || "◇"}</span>
                          <span className="text-[10px] uppercase tracking-widest">{e.country}</span>
                          <span className="text-[9px] uppercase tracking-widest ml-auto flex items-center gap-1">
                            {e.impact === "high" && <Flame size={9} />}
                            {e.impact}
                          </span>
                        </div>
                        <p className="font-body text-sm text-neutral-200 leading-tight">{e.event}</p>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-neutral-500">
                          <span>{(e.time || "").slice(11, 16)} UTC</span>
                          {e.estimate && <span>est: <span className="text-neutral-300">{e.estimate}</span></span>}
                          {e.prev && <span>prev: <span className="text-neutral-300">{e.prev}</span></span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      {/* BIG CHART MODAL — full-viewport streaming TradingView chart */}
      {bigChart && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm p-4 md:p-6 flex flex-col"
             data-testid="big-chart-modal"
             onClick={(e) => { if (e.target === e.currentTarget) setBigChart(null); }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-[#D4AF37] text-xl">◇</span>
              <div>
                <p className="overline text-[#D4AF37]">Live Chart · Streaming</p>
                <h3 className="font-display text-2xl leading-none mt-1">{bigChart.label}</h3>
              </div>
              <span className="hidden md:inline text-[9px] uppercase tracking-widest text-neutral-500 ml-4">
                {bigChart.symbol}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden lg:flex items-center gap-1 mr-2 flex-wrap max-w-2xl">
                {CHART_SYMBOLS.map((s) => (
                  <button key={s.symbol}
                    onClick={() => setBigChart(s)}
                    data-testid={`bigchart-switch-${s.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition ${
                      bigChart.symbol === s.symbol
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-[#1f1f1f] text-neutral-500 hover:text-neutral-200 hover:border-neutral-500"
                    }`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <button onClick={() => setBigChart(null)}
                      data-testid="big-chart-close"
                      className="text-neutral-400 hover:text-[#D4AF37] border border-[#1f1f1f] hover:border-[#D4AF37] px-3 py-2">
                <X size={16} />
              </button>
            </div>
          </div>
          {/* Mobile symbol switcher */}
          <div className="lg:hidden mb-3 flex gap-1 overflow-x-auto pb-1">
            {CHART_SYMBOLS.map((s) => (
              <button key={s.symbol}
                onClick={() => setBigChart(s)}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition whitespace-nowrap ${
                  bigChart.symbol === s.symbol
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                    : "border-[#1f1f1f] text-neutral-500"
                }`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex-1 border border-[#1f1f1f] overflow-hidden">
            <TradingViewGoldChart
              key={bigChart.symbol}
              symbol={bigChart.symbol}
              interval="60"
              height="100%"
              autosize
            />
          </div>
        </div>
      )}
    </Layout>
  );
}
